// frontend/src/utils/validationRules.js
// 
// バリデーションルール定義
// バックエンド（Rails）のバリデーションと同期させること
//

/**
 * バリデーションルールの構造:
 * {
 *   fieldName: {
 *     required: boolean,
 *     label: string,
 *     errorMessages: { errorKey: errorMessage },
 *     validate: (value, formData?) => errorKey | null
 *   }
 * }
 */

// ========================================
// ユーザー（患者）情報のバリデーション
// ========================================
const userValidationRules = {
  last_name: {
    required: true,
    label: '氏名（姓）',
    errorMessages: {
      required: '氏名（姓）は必須です',
      maxLength: '氏名（姓）は50文字以内で入力してください'
    },
    validate: (value) => {
      if (!value || value.toString().trim().length === 0) return 'required';
      if (value.length > 50) return 'maxLength';
      return null;
    }
  },

  first_name: {
    required: true,
    label: '氏名（名）',
    errorMessages: {
      required: '氏名（名）は必須です',
      maxLength: '氏名（名）は50文字以内で入力してください'
    },
    validate: (value) => {
      if (!value || value.toString().trim().length === 0) return 'required';
      if (value.length > 50) return 'maxLength';
      return null;
    }
  },

  last_name_kana: {
    required: false,
    label: 'フリガナ（姓）',
    errorMessages: {
      maxLength: 'フリガナ（姓）は50文字以内で入力してください'
    },
    validate: (value) => {
      if (!value) return null;
      if (value.length > 50) return 'maxLength';
      return null;
    }
  },

  first_name_kana: {
    required: false,
    label: 'フリガナ（名）',
    errorMessages: {
      maxLength: 'フリガナ（名）は50文字以内で入力してください'
    },
    validate: (value) => {
      if (!value) return null;
      if (value.length > 50) return 'maxLength';
      return null;
    }
  },

  email_user: {
    required: true,
    label: 'メールアドレス',
    errorMessages: {
      required: 'メールアドレスは必須です',
      format: 'メールアドレスの形式が正しくありません'
    },
    validate: (value) => {
      if (!value || value.toString().trim().length === 0) return 'required';
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) return 'format';
      return null;
    }
  },

  phone_number: {
    required: true,
    label: '電話番号',
    errorMessages: {
      required: '電話番号は必須です',
      format: '電話番号は10桁または11桁の数字で入力してください（ハイフンなし）'
    },
    validate: (value) => {
      if (!value || value.toString().trim().length === 0) return 'required';
      // ハイフンや空白を除去
      const digits = value.toString().replace(/[-\s]/g, '');
      if (!/^\d{10,11}$/.test(digits)) return 'format';
      return null;
    }
  },

  birth_date: {
    required: true,
    label: '生年月日',
    errorMessages: {
      required: '生年月日は必須です',
      format: '生年月日の形式が正しくありません'
    },
    validate: (value) => {
      if (!value) return 'required';
      // Date型またはYYYY-MM-DD形式の文字列をチェック
      if (value instanceof Date) return null;
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
      return 'format';
    }
  },

  gender: {
    required: true,
    label: '性別',
    errorMessages: {
      required: '性別は必須です'
    },
    validate: (value) => {
      if (!value || value.toString().trim().length === 0) return 'required';
      return null;
    }
  },

  prefecture: {
    required: true,
    label: '都道府県',
    errorMessages: {
      required: '都道府県は必須です'
    },
    validate: (value) => {
      if (!value || value.toString().trim().length === 0) return 'required';
      return null;
    }
  },

  height: {
    required: true,
    label: '身長',
    errorMessages: {
      required: '身長は必須です',
      range: '身長は50〜250cmの範囲で入力してください',
      format: '身長は数値で入力してください'
    },
    validate: (value) => {
      if (!value || value.toString().trim().length === 0) return 'required';
      const num = parseFloat(value);
      if (isNaN(num)) return 'format';
      if (num < 50 || num > 250) return 'range';
      return null;
    }
  },

  weight: {
    required: true,
    label: '体重',
    errorMessages: {
      required: '体重は必須です',
      range: '体重は2〜500kgの範囲で入力してください',
      format: '体重は数値で入力してください'
    },
    validate: (value) => {
      if (!value || value.toString().trim().length === 0) return 'required';
      const num = parseFloat(value);
      if (isNaN(num)) return 'format';
      if (num < 2 || num > 500) return 'range';
      return null;
    }
  },

  allergies: {
    required: true,
    label: 'アレルギー',
    errorMessages: {
      required: 'アレルギーの有無を選択してください（「なし」でも構いません）',
      maxLength: 'アレルギーは500文字以内で入力してください'
    },
    validate: (value) => {
      if (!value || value.toString().trim().length === 0) return 'required';
      if (value.length > 500) return 'maxLength';
      return null;
    }
  },

  allergy_details: {
    required: false,
    label: 'アレルギー詳細',
    errorMessages: {
      maxLength: 'アレルギー詳細は500文字以内で入力してください'
    },
    validate: (value) => {
      if (!value) return null;
      if (value.length > 500) return 'maxLength';
      return null;
    }
  },

  medical_history: {
    required: false,
    label: '既往歴',
    errorMessages: {
      maxLength: '既往歴は1000文字以内で入力してください'
    },
    validate: (value) => {
      if (!value) return null;
      if (value.length > 1000) return 'maxLength';
      return null;
    }
  },

  current_medications: {
    required: false,
    label: '現在服用中の薬',
    errorMessages: {
      maxLength: '現在服用中の薬は1000文字以内で入力してください'
    },
    validate: (value) => {
      if (!value) return null;
      if (value.length > 1000) return 'maxLength';
      return null;
    }
  },

  pregnancy_status: {
    required: false,
    label: '妊娠状況',
    errorMessages: {},
    validate: () => null
  }
};

// ========================================
// 配送先住所のバリデーション
// ========================================
const deliveryAddressValidationRules = {
  postal_code: {
    required: true,
    label: '郵便番号',
    errorMessages: {
      required: '郵便番号は必須です',
      format: '郵便番号は7桁の数字で入力してください（ハイフンなし）'
    },
    validate: (value) => {
      if (!value || value.toString().trim().length === 0) return 'required';
      // ハイフンを除去
      const digits = value.toString().replace(/-/g, '');
      if (!/^\d{7}$/.test(digits)) return 'format';
      return null;
    }
  },

  prefecture: {
    required: true,
    label: '都道府県',
    errorMessages: {
      required: '都道府県は必須です'
    },
    validate: (value) => {
      if (!value || value.toString().trim().length === 0) return 'required';
      return null;
    }
  },

  city: {
    required: true,
    label: '市区町村',
    errorMessages: {
      required: '市区町村は必須です',
      maxLength: '市区町村は100文字以内で入力してください'
    },
    validate: (value) => {
      if (!value || value.toString().trim().length === 0) return 'required';
      if (value.length > 100) return 'maxLength';
      return null;
    }
  },

  address_line1: {
    required: true,
    label: '町名・番地',
    errorMessages: {
      required: '町名・番地は必須です',
      maxLength: '町名・番地は200文字以内で入力してください'
    },
    validate: (value) => {
      if (!value || value.toString().trim().length === 0) return 'required';
      if (value.length > 200) return 'maxLength';
      return null;
    }
  },

  address_line2: {
    required: false,
    label: '建物名・部屋番号',
    errorMessages: {
      maxLength: '建物名・部屋番号は200文字以内で入力してください'
    },
    validate: (value) => {
      if (!value) return null;
      if (value.length > 200) return 'maxLength';
      return null;
    }
  }
};

// ========================================
// マイナ保険証情報・資格確認書のバリデーション
// ========================================
const insuranceCardValidationRules = {
  front_image: {
    required: false, // 初回登録時は必須だが、更新時は任意
    label: '資格確認証・マイナ保険情報（表・1枚目）',
    errorMessages: {
      required: '資格確認証・マイナ保険情報（表・1枚目）の画像をアップロードしてください',
      fileType: '画像ファイル（JPEG、PNG）のみアップロード可能です',
      fileSize: 'ファイルサイズは10MB以下にしてください'
    },
    validate: (file) => {
      if (!file) return null; // 任意の場合
      
      // ファイルタイプチェック
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) return 'fileType';
      
      // ファイルサイズチェック（10MB）
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) return 'fileSize';
      
      return null;
    }
  },

  back_image: {
    required: false,
    label: '資格確認証・マイナ保険情報（裏・2枚目）',
    errorMessages: {
      fileType: '画像ファイル（JPEG、PNG）のみアップロード可能です',
      fileSize: 'ファイルサイズは10MB以下にしてください'
    },
    validate: (file) => {
      if (!file) return null;
      
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) return 'fileType';
      
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) return 'fileSize';
      
      return null;
    }
  },

  // マイナ保険証情報・資格確認書アップロード時の特別ルール
  insurance_upload: {
    required: true,
    label: '資格確認証・マイナ保険情報',
    errorMessages: {
      required: '表・1枚目または裏・2枚目のどちらか一方は必須です'
    },
    validate: (value, formData) => {
      // formData = { front: File|null, back: File|null }
      const hasFront = formData?.front instanceof File;
      const hasBack = formData?.back instanceof File;
      
      if (!hasFront && !hasBack) return 'required';
      return null;
    }
  }
};

// ========================================
// エクスポート
// ========================================
export const validationRules = {
  user: userValidationRules,
  delivery_address: deliveryAddressValidationRules,
  insurance_card: insuranceCardValidationRules
};

// 個別エクスポート（後方互換性のため）
export { userValidationRules };
export { deliveryAddressValidationRules };
export { insuranceCardValidationRules };

// 新規登録用の簡易版バリデーション（郵便番号 + 住所 + 建物名のみ）
export const deliveryAddressSimpleValidationRules = {
  postal_code: {
    required: true,
    label: '郵便番号',
    errorMessages: {
      required: '郵便番号は必須です',
      format: '郵便番号は7桁の数字で入力してください（ハイフンなし）'
    },
    validate: (value) => {
      if (!value || value.toString().trim().length === 0) return 'required';
      const digits = value.toString().replace(/-/g, '');
      if (!/^\d{7}$/.test(digits)) return 'format';
      return null;
    }
  },

  address: {
    required: true,
    label: '住所',
    errorMessages: {
      required: '住所は必須です',
      maxLength: '住所は500文字以内で入力してください'
    },
    validate: (value) => {
      if (!value || value.toString().trim().length === 0) return 'required';
      if (value.length > 500) return 'maxLength';
      return null;
    }
  },

  address_line2: {
    required: false,
    label: '建物名・部屋番号',
    errorMessages: {
      maxLength: '建物名・部屋番号は200文字以内で入力してください'
    },
    validate: (value) => {
      if (!value) return null;
      if (value.length > 200) return 'maxLength';
      return null;
    }
  }
};

// ヘルパー関数: 全フィールドのリストを取得
export const getRequiredFields = (entityType) => {
  const rules = validationRules[entityType];
  if (!rules) return [];
  
  return Object.keys(rules).filter(key => rules[key].required);
};

// ヘルパー関数: 必須項目がすべて入力されているかチェック
export const areRequiredFieldsFilled = (entityType, formData) => {
  const requiredFields = getRequiredFields(entityType);
  
  return requiredFields.every(field => {
    const value = formData[field];
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim().length === 0) return false;
    return true;
  });
};

export default validationRules;
